// Hand-written normalize/validate helpers, following the same style as
// src/components/raffle/raffleFormUtils.ts (this codebase doesn't use a schema-validation
// library like zod/yup for hand-built forms).
import { DEFAULT_PRIZES, MAX_BONUS_SPINS_PER_PRIZE, WheelConfigFormData, WheelPrizeFormData, WheelPrizeType } from "./wheelPrizeTypes"

const toPositiveIntOrNull = (value: unknown): number | null => {
  const num = Number(value)
  return Number.isFinite(num) && num > 0 ? Math.floor(num) : null
}

const toMillisOrNull = (value: unknown): number | null => {
  const num = Number(value)
  return Number.isFinite(num) && num > 0 ? Math.floor(num) : null
}

const isSafeHttpUrl = (value: string): boolean => {
  try {
    const parsed = new URL(value)
    return parsed.protocol === "http:" || parsed.protocol === "https:"
  } catch {
    return false
  }
}

// Mirrors functions/src/models/WheelPrize.ts's `isLikelyLosingLabel` (kept in sync manually).
// Used only as the default for `winning` when a loaded prize doesn't set it explicitly, so
// opening the editor against an existing "Sorry, try again." style prize pre-fills the
// checkbox unchecked instead of requiring the admin to notice and fix every prize by hand.
const NO_PRIZE_LABEL_PATTERN = /\b(?:try\s+again|sigue\s+intentando|no\s+prize|sin\s+premio)\b/i

export const isLikelyLosingLabel = (label: string | null | undefined, shortLabel?: string | null): boolean =>
  [label, shortLabel].some((candidate) => typeof candidate === "string" && NO_PRIZE_LABEL_PATTERN.test(candidate))

// Prize entries written before prize types existed only ever had
// `{id, label, points, weight, imageUrl}` — those load as `facilCoins` here too, so opening
// this screen against an old config doesn't require a manual migration step.
export const normalizeLoadedPrize = (raw: any, index: number): WheelPrizeFormData => {
  const record = raw && typeof raw === "object" ? raw : {}
  const type: WheelPrizeType =
    typeof record.type === "string" ? record.type : Number.isFinite(Number(record.points)) ? "facilCoins" : "custom"
  const label = typeof record.label === "string" ? record.label : ""
  const shortLabel = typeof record.shortLabel === "string" ? record.shortLabel : null

  return {
    id: typeof record.id === "string" && record.id.trim() ? record.id.trim() : `prize-${index}`,
    type,
    label,
    shortLabel,
    description: typeof record.description === "string" ? record.description : null,
    imageUrl: typeof record.imageUrl === "string" && record.imageUrl ? record.imageUrl : null,
    weight: Number.isFinite(Number(record.weight)) && Number(record.weight) > 0 ? Number(record.weight) : 1,
    enabled: record.enabled !== false,
    sortOrder: Number.isFinite(Number(record.sortOrder)) ? Number(record.sortOrder) : index,
    announceWinner: record.announceWinner === true ? true : record.announceWinner === false ? false : null,
    featured: record.featured === true,
    grandPrize: record.grandPrize === true,
    winning: record.winning === true ? true : record.winning === false ? false : !isLikelyLosingLabel(label, shortLabel),
    startAtMillis: toMillisOrNull(record.startAtMillis),
    endAtMillis: toMillisOrNull(record.endAtMillis),
    totalQuantity: toPositiveIntOrNull(record.totalQuantity),
    quantityPerDay: toPositiveIntOrNull(record.quantityPerDay),
    perUserLimit: toPositiveIntOrNull(record.perUserLimit),
    amount: Number.isFinite(Number(record.amount))
      ? Number(record.amount)
      : Number.isFinite(Number(record.points))
        ? Number(record.points)
        : type === "facilCoins" || type === "cash"
          ? 0
          : undefined,
    sponsorId: typeof record.sponsorId === "string" ? record.sponsorId : undefined,
    serviceId: typeof record.serviceId === "string" ? record.serviceId : undefined,
    serviceTitle: typeof record.serviceTitle === "string" ? record.serviceTitle : null,
    raffleId: typeof record.raffleId === "string" ? record.raffleId : undefined,
    ticketsCount: toPositiveIntOrNull(record.ticketsCount) ?? (type === "raffleEntry" ? 1 : undefined),
    currency: typeof record.currency === "string" ? record.currency.toUpperCase() : type === "cash" ? "USD" : undefined,
    fulfillmentInstructions: typeof record.fulfillmentInstructions === "string" ? record.fulfillmentInstructions : null,
    offerId: typeof record.offerId === "string" ? record.offerId : null,
    offerTitle: typeof record.offerTitle === "string" ? record.offerTitle : null,
    spinsAwarded: toPositiveIntOrNull(record.spinsAwarded) ?? (type === "bonusSpin" ? 1 : undefined),
    ctaLabel: typeof record.ctaLabel === "string" ? record.ctaLabel : null,
    referenceUrl: typeof record.referenceUrl === "string" ? record.referenceUrl : null,
    adminNotes: typeof record.adminNotes === "string" ? record.adminNotes : null,
  }
}

export const normalizeLoadedPrizes = (value: unknown): WheelPrizeFormData[] => {
  if (!Array.isArray(value) || value.length === 0) {
    return DEFAULT_PRIZES
  }
  return value.map((entry, index) => normalizeLoadedPrize(entry, index))
}

export const calculateOddsPercent = (prizes: WheelPrizeFormData[]): Map<string, number> => {
  const totalWeight = prizes
    .filter((prize) => prize.enabled)
    .reduce((sum, prize) => sum + (prize.weight > 0 ? prize.weight : 0), 0)

  const odds = new Map<string, number>()
  prizes.forEach((prize) => {
    odds.set(prize.id, prize.enabled && totalWeight > 0 ? (Math.max(0, prize.weight) / totalWeight) * 100 : 0)
  })
  return odds
}

export const DOMINANT_ODDS_THRESHOLD_PERCENT = 60

// A prize with no caps and no date window can never run out, so the wheel can guarantee a
// candidate always exists — mirrors `isAlwaysEligible` in functions/src/models/WheelPrize.ts.
export const isAlwaysEligiblePrize = (prize: WheelPrizeFormData): boolean =>
  prize.enabled &&
  prize.startAtMillis === null &&
  prize.endAtMillis === null &&
  prize.totalQuantity === null &&
  prize.quantityPerDay === null &&
  prize.perUserLimit === null

export type PrizeFieldErrors = Partial<Record<string, string>>

export const validatePrize = (prize: WheelPrizeFormData): PrizeFieldErrors => {
  const errors: PrizeFieldErrors = {}

  if (!prize.label.trim()) {
    errors.label = "Label is required."
  }
  if (!Number.isFinite(prize.weight) || prize.weight <= 0) {
    errors.weight = "Weight must be greater than 0."
  }

  switch (prize.type) {
    case "facilCoins":
      if (!Number.isFinite(prize.amount) || (prize.amount ?? -1) < 0) {
        errors.amount = "Enter a non-negative amount."
      }
      break
    case "product":
      if (!prize.sponsorId || !prize.serviceId) {
        errors.serviceId = "Select a sponsor service."
      }
      break
    case "raffleEntry":
      if (!prize.raffleId) {
        errors.raffleId = "Select a raffle."
      }
      if (!Number.isFinite(prize.ticketsCount) || (prize.ticketsCount ?? 0) <= 0) {
        errors.ticketsCount = "Enter a positive tickets count."
      }
      break
    case "cash":
      if (!Number.isFinite(prize.amount) || (prize.amount ?? 0) <= 0) {
        errors.amount = "Enter a positive amount."
      }
      if (!prize.currency?.trim()) {
        errors.currency = "Currency is required."
      }
      break
    case "coupon":
      if (!prize.sponsorId) {
        errors.sponsorId = "Sponsor is required."
      }
      if (!prize.offerId && !prize.serviceId) {
        errors.offerId = "Select an offer or a service to grant a coupon for."
      }
      break
    case "bonusSpin":
      if (!Number.isFinite(prize.spinsAwarded) || (prize.spinsAwarded ?? 0) <= 0) {
        errors.spinsAwarded = "Enter a positive number of spins."
      } else if ((prize.spinsAwarded ?? 0) > MAX_BONUS_SPINS_PER_PRIZE) {
        errors.spinsAwarded = `Cannot exceed ${MAX_BONUS_SPINS_PER_PRIZE} spins per prize.`
      }
      break
    case "custom":
      if (prize.referenceUrl && !isSafeHttpUrl(prize.referenceUrl)) {
        errors.referenceUrl = "Enter a valid http:// or https:// URL."
      }
      break
    default:
      break
  }

  if (prize.totalQuantity !== null && prize.totalQuantity <= 0) {
    errors.totalQuantity = "Must be a positive number, or left blank for unlimited."
  }
  if (prize.quantityPerDay !== null && prize.quantityPerDay <= 0) {
    errors.quantityPerDay = "Must be a positive number, or left blank for unlimited."
  }
  if (prize.perUserLimit !== null && prize.perUserLimit <= 0) {
    errors.perUserLimit = "Must be a positive number, or left blank for unlimited."
  }
  if (prize.startAtMillis !== null && prize.endAtMillis !== null && prize.startAtMillis > prize.endAtMillis) {
    errors.endAtMillis = "End date must be after the start date."
  }

  return errors
}

export const validateConfig = (config: WheelConfigFormData): string[] => {
  const errors: string[] = []
  const enabledPrizes = config.prizes.filter((prize) => prize.enabled)

  if (enabledPrizes.length === 0) {
    errors.push("At least one prize must be enabled.")
  }

  const totalWeight = enabledPrizes.reduce((sum, prize) => sum + (prize.weight > 0 ? prize.weight : 0), 0)
  if (enabledPrizes.length > 0 && totalWeight <= 0) {
    errors.push("The total weight of enabled prizes must be greater than zero.")
  }

  const ids = config.prizes.map((prize) => prize.id)
  if (new Set(ids).size !== ids.length) {
    errors.push("Prize IDs must be unique.")
  }

  if (enabledPrizes.length > 0 && !enabledPrizes.some(isAlwaysEligiblePrize)) {
    errors.push(
      "At least one enabled prize must have no inventory caps and no date window — otherwise every prize could run out at once and a spin would have nothing to land on.",
    )
  }

  if (config.dailySpinLimit <= 0) {
    errors.push("Daily spin limit must be at least 1.")
  }
  if (config.cooldownMinutes < 0) {
    errors.push("Cooldown minutes cannot be negative.")
  }
  if (
    config.campaignStartAtMillis !== null &&
    config.campaignEndAtMillis !== null &&
    config.campaignStartAtMillis > config.campaignEndAtMillis
  ) {
    errors.push("Campaign end date must be after the campaign start date.")
  }

  config.prizes.forEach((prize) => {
    const prizeErrors = validatePrize(prize)
    if (Object.keys(prizeErrors).length > 0) {
      errors.push(`"${prize.label || prize.id}": ${Object.values(prizeErrors).join(" ")}`)
    }
  })

  return errors
}

// Strips fields that don't apply to the prize's own type before writing to Firestore, and
// normalizes optional values to `null` (never `undefined`, which Firestore rejects).
export const cleanPrizeForSave = (prize: WheelPrizeFormData): Record<string, unknown> => {
  const base: Record<string, unknown> = {
    id: prize.id,
    type: prize.type,
    label: prize.label.trim(),
    shortLabel: prize.shortLabel || null,
    description: prize.description || null,
    imageUrl: prize.imageUrl || null,
    weight: prize.weight,
    enabled: prize.enabled,
    sortOrder: prize.sortOrder,
    announceWinner: prize.announceWinner,
    featured: prize.featured,
    grandPrize: prize.grandPrize,
    winning: prize.winning,
    startAtMillis: prize.startAtMillis,
    endAtMillis: prize.endAtMillis,
    totalQuantity: prize.totalQuantity,
    quantityPerDay: prize.quantityPerDay,
    perUserLimit: prize.perUserLimit,
  }

  switch (prize.type) {
    case "facilCoins":
      return { ...base, amount: prize.amount ?? 0 }
    case "product":
      return { ...base, sponsorId: prize.sponsorId, serviceId: prize.serviceId, serviceTitle: prize.serviceTitle || null }
    case "raffleEntry":
      return { ...base, raffleId: prize.raffleId, ticketsCount: prize.ticketsCount ?? 1 }
    case "cash":
      return {
        ...base,
        amount: prize.amount ?? 0,
        currency: (prize.currency || "USD").toUpperCase(),
        fulfillmentInstructions: prize.fulfillmentInstructions || null,
      }
    case "coupon":
      return {
        ...base,
        sponsorId: prize.sponsorId,
        offerId: prize.offerId || null,
        serviceId: prize.serviceId || null,
        offerTitle: prize.offerTitle || null,
      }
    case "bonusSpin":
      return { ...base, spinsAwarded: Math.min(MAX_BONUS_SPINS_PER_PRIZE, Math.max(1, prize.spinsAwarded ?? 1)) }
    case "custom":
      return {
        ...base,
        ctaLabel: prize.ctaLabel || null,
        referenceUrl: prize.referenceUrl || null,
        fulfillmentInstructions: prize.fulfillmentInstructions || null,
        adminNotes: prize.adminNotes || null,
      }
    default:
      return base
  }
}
